import moment from 'moment-timezone';
import _ from 'lodash';
import axios from 'axios';
import Region from '../../constants/region';
import { getExpressAPIHost, getUser, isTCJRegion } from '../../../utils';
import { isLTValid, isStringInList, isDateTime, isNumber, uniqueError, validateError } from '../csv-upload/UploadCSV.helpers';
import { getUserDisplayName, getUserLicenseNumber } from '../../helpers/users/utils';

export const propertyToString = (commodities, property) => {
    return commodities[property] || null;
};

export const stringToProperty = (commodities, property) => {
    return _.invert(commodities)[property] || null;
};

export const filterById = (array) => array.filter((item) => item.id !== '');

export const mapValuesById = (ids, groupMap) => ids.map((id) => groupMap[id]);

export function toAmountString(amount) {
    if (!amount?.toString()) return 0;
    return `${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

export function toTimeString(timeInMs) {
    const hours = Math.floor(timeInMs / (1000 * 60 * 60));
    const minutes = Math.floor(timeInMs / (1000 * 60)) % 60;
    const seconds = (Math.floor(timeInMs / 1000) % 60) % 60;
    const hh = !!hours ? hours.toString() : '';
    const mm = !!hh ? minutes.toString().padStart(2, '0') : minutes.toString();
    const ss = seconds.toString().padStart(2, '0');
    return `${!!hh ? `${hh}:` : ''}${mm}:${ss}`;
}

export function getDateArray(date) {
    return [...Array(7).keys()].map((i) => date.clone().add(i, 'days'));
}

export function getDataMap(data, demand) {
    return demand ? data.demandItemDateMap : data.supplyItemDateMap;
}

export function getProcessors(data, processorIds) {
    if (processorIds.length === 0) return data.processors;
    return processorIds.map((pId) => data.processorMap[pId]);
}

export function getHaulingCompanies(data, haulingIds) {
    if (haulingIds.length === 0) return data.haulingCompanies;
    return haulingIds.map((hId) => data.haulingMap[hId]);
}

export function getMaxSingleDayItems(data, currentDay) {
    return 1;
}

export function getMaxItems(dataMap) {
    return Math.max(...Object.values(dataMap).map((value) => (value.items ? value.items.length : 0)));
}

export function filterSearch(items, search, field) {
    if (search === '') return items;
    return items.filter((item) => item[field]?.name?.toLowerCase().includes(search.toLowerCase()));
}

export const getPastDateOffset = (type) => {
    const { region } = getUser();

    if (isTCJRegion(region) || [Region.IMP].includes(region)) return 7;
    if (region === Region.PRAIRIE) return 6;
    if (region === Region.UDA) return type === 'fluid' ? 45 : 1;

    return 1;
};

export function isPastDate(date, type) {
    const offset = getPastDateOffset(type);
    const referenceDate = moment().subtract(offset, 'day');
    return moment.isMoment(date) ? date.isBefore(referenceDate, 'day') : moment(date, 'YYYY-MM-DD').isBefore(referenceDate, 'day');
}

export const internalUserStructToSearchableSelectStruct = (userSet) =>
    userSet.map((u) => {
        const license_number = getUserLicenseNumber(u);
        const name = getUserDisplayName(u.name, license_number, false);
        return { name, value: u.id };
    });

export const RawMilkCSVConfig = (routes, processors, haulers) => {
    const { region } = getUser();
    const uploadDateFormat = getUploadDateFormat(region);
    if (region === Region.MMPA) {
        return {
            headers: [
                {
                    name: 'LT Number',
                    inputName: 'lt_number',
                    unique: true,
                    uniqueError,
                    validate: isLTValid,
                    validateError,
                },
                {
                    name: 'Start Date',
                    inputName: 'start_date',
                    required: true,
                    validate: (value) => isDateTime(value, uploadDateFormat),
                    validateError,
                },
                {
                    name: 'End Date',
                    inputName: 'end_date',
                    required: true,
                    validate: (value) => isDateTime(value, uploadDateFormat),
                    validateError,
                },
                {
                    name: 'Amount',
                    inputName: 'amount',
                    required: true,
                    validate: (value) => isNumber(value),
                    validateError,
                },
                {
                    name: 'Route',
                    inputName: 'route_name',
                    required: true,
                    validate: (value) => isStringInList(value, routes),
                    validateError,
                },
                {
                    name: 'Processor',
                    inputName: 'processor_name',
                    validate: (value) => isStringInList(value, processors, 'name', true),
                    validateError,
                },
                {
                    name: 'Hauling',
                    inputName: 'hauling_name',
                    validate: (value) => isStringInList(value, haulers, 'name', true),
                    validateError,
                },
                {
                    name: 'Milk Category',
                    inputName: 'milk_category',
                    validate: (value) => isStringInList(value, milkCategories, 'name', true),
                    validateError,
                },
                {
                    name: 'Load Number',
                    inputName: 'po_number',
                },
                {
                    name: 'Status',
                    inputName: 'status',
                },
                {
                    name: 'Notes',
                    inputName: 'notes',
                },
            ],
            options: {},
        };
    }
    return {
        headers: [
            {
                name: 'LT Number',
                inputName: 'lt_number',
                required: true,
                unique: true,
                uniqueError,
                validate: isLTValid,
                validateError,
            },
            {
                name: 'Route',
                inputName: 'route_name',
                required: true,
                validate: (value) => isStringInList(value, routes),
                validateError,
            },
            {
                name: 'Processor',
                inputName: 'processor_name',
                required: true,
                validate: (value) => isStringInList(value, processors),
                validateError,
            },
            ...(region === Region.CDI
                ? [
                      {
                          name: 'Classification',
                          inputName: 'milk_classification',
                      },
                  ]
                : []),
        ],
        options: {},
    };
};

const uploadDateFormatMap = {
    [Region.MMPA]: 'YYYY-MM-DD HH:mm',
    default: 'M/D/YY H:mm',
};

export const getUploadDateFormat = (region) => {
    return uploadDateFormatMap[region] ?? uploadDateFormatMap.default;
};

export const ProcessedFluidCSVConfig = () => {
    const { region } = getUser();
    const uploadDateFormat = getUploadDateFormat(region);
    return {
        headers: [
            {
                name: 'Hauler Pickup Time',
                inputName: 'start_date',
                required: true,
                validate: (value) => moment(value, uploadDateFormat, true).isValid(),
                validateError,
            },
            {
                name: 'Customer Delivery Time',
                inputName: 'date',
                required: true,
                validate: (value) => moment(value, uploadDateFormat, true).isValid(),
                validateError,
            },
            {
                name: 'Plant Load Out Time',
                inputName: 'pickup_ready_time',
                required: true,
                validate: (value) => moment(value, uploadDateFormat, true).isValid(),
                validateError,
            },
            {
                name: 'Amount',
                inputName: 'amount',
                required: true,
                validateError,
            },
            {
                name: 'Commodity',
                inputName: 'commodity_type',
                required: true,
                validateError,
            },
            {
                name: 'Destination Processor',
                inputName: 'destination_processor_name',
                required: true,
            },
            {
                name: 'Origin Processor',
                inputName: 'origin_processor_name',
                required: true,
            },
            {
                name: 'Hauling',
                inputName: 'hauler_name',
                required: true,
            },
            {
                name: 'LT Number',
                inputName: 'lt_number',
                validate: isLTValid,
                validateError,
                // Validation will be done entirely on backend
            },
            ...([Region.UDA, Region.CDI].includes(region)
                ? [
                      {
                          name: 'PO Number',
                          inputName: 'po_number',
                      },
                  ]
                : []),
            ...(region === Region.UDA
                ? [
                      {
                          name: 'Manifest Number',
                          inputName: 'manifest_number',
                      },
                  ]
                : []),
            ...(region === Region.CDI
                ? [
                      {
                          name: 'Note',
                          inputName: 'note',
                      },
                  ]
                : []),
        ],
        options: {
            hasDateColumn: true,
        },
    };
};

export const submitCSV = async (endpoint, formData) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${getExpressAPIHost()}${endpoint}`,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response;
    } catch (error) {
        return { error: error?.response?.data?.message || 'Unable to upload CSV.' };
    }
};

export const getDateString = (start, end) => {
    const sameMonth = start.isSame(end, 'month');
    const sameYear = start.isSame(end, 'year');

    const startMonth = start.format('MMMM');
    const startYear = start.format('YYYY');
    const endMonthYear = end.format('MMMM YYYY');

    return sameMonth ? `${startMonth} ${startYear}` : `${startMonth}${sameYear ? '' : ` ${startYear}`} - ${endMonthYear}`;
};

export const milkCategories = [
    { name: 'Member Producer', value: 'member_producer' },
    { name: 'Outside Milk', value: 'outside_milk' },
    { name: 'Co-mingled', value: 'comingled' },
];

export const filterCategoryOptions = (mode) => {
    if (!mode) {
        return milkCategories;
    }

    return milkCategories.filter((option) => option.value === mode.replace('-', '_') || option.value === 'comingled');
};

export const getInitialCategoryValue = (mode) => {
    return milkCategories.filter((option) => option.value === mode.replace('-', '_'));
};

export const filterCategoriesByValue = (selectedCategories) => {
    return milkCategories.filter((milkCategory) => selectedCategories.some((selected) => selected.value === milkCategory.value));
};
